import { THEME } from '../assets/globalConstants';

/* =====================================================================
   brandTheme.js — dashboard-driven brand theme with no-flash bootstrap
   ---------------------------------------------------------------------
   The active brand theme (blueberry/jade/mexican/platform/red-orange/azel) is set
   by admins in the dashboard and stored in the backend (settings.theme).
   Backend settings arrive async over the socket, which would flash the
   default theme on load — so we CACHE the theme in localStorage and apply
   it synchronously before first paint (same approach colorMode.js uses for
   dark mode). public/constants.js THEME is the build-time FALLBACK, used on
   the very first visit before any cache exists.
   ===================================================================== */

export const VALID_THEMES = ['blueberry', 'jade', 'mexican', 'platform', 'red-orange', 'azel'];
const STORAGE_KEY = 'fdwa_theme';

// The hard default theme — used for backwards compatibility when NEITHER the
// dashboard setting (settings.theme → cache) NOR constants.js THEME yields a
// valid theme: e.g. older deployments that predate the theme setting (no
// settings.theme and no `var THEME` in constants.js → window.THEME undefined),
// or a stale/renamed value (like the former "wolt"). Always lands on blueberry.
export const DEFAULT_THEME = 'blueberry';

// constants.js THEME (window.THEME) is the build-time fallback; guarded so a
// missing or unknown value degrades to DEFAULT_THEME (blueberry).
export const FALLBACK_THEME = VALID_THEMES.includes(THEME) ? THEME : DEFAULT_THEME;

export const getStoredTheme = () => {
  try {
    const t = localStorage.getItem(STORAGE_KEY);
    return VALID_THEMES.includes(t) ? t : null;
  } catch (e) {
    return null;
  }
};

export const setStoredTheme = (theme) => {
  try {
    if (VALID_THEMES.includes(theme)) localStorage.setItem(STORAGE_KEY, theme);
  } catch (e) {
    /* storage disabled / private mode — ignore */
  }
};

// What to paint with right now: the cached (dashboard-driven) theme, else the
// constants.js fallback.
export const resolveInitialTheme = () => getStoredTheme() || FALLBACK_THEME;

// Apply the brand theme class to <html> WITHOUT disturbing the dark-mode class.
export const applyThemeClass = (theme) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  VALID_THEMES.forEach((t) => root.classList.remove(t));
  root.classList.add(VALID_THEMES.includes(theme) ? theme : FALLBACK_THEME);
};

// Pre-paint bootstrap — call at module load in main.js BEFORE React renders.
export const bootstrapTheme = () => {
  applyThemeClass(resolveInitialTheme());
};

// Reconcile the backend setting with what's painted. Because brand colors are
// resolved in JS at mount (useThemeColor / the MUI theme), a mid-session swap
// is unreliable — so when the dashboard theme differs from what's showing, we
// cache it and reload ONCE (the reload re-applies the new theme synchronously
// via bootstrapTheme). When it matches, just keep the cache fresh — no reload.
// In practice this only reloads right after an admin changes the theme; normal
// repeat loads never do.
export const syncThemeFromSettings = (settingsTheme) => {
  if (!VALID_THEMES.includes(settingsTheme)) return;
  if (settingsTheme === resolveInitialTheme()) {
    setStoredTheme(settingsTheme);
    return;
  }
  setStoredTheme(settingsTheme);
  // Only reload if the cache actually persisted. If localStorage is unavailable
  // (private mode / blocked), the cache can't stick — reloading would loop
  // forever (it'd keep resolving to the fallback and mismatching). In that case
  // skip the reload and just stay on the fallback theme for the session.
  if (getStoredTheme() === settingsTheme) {
    try { window.location.reload(); } catch (e) { /* ignore */ }
  }
};
